import type { components, Page } from "@theta/api-client";
import type { GroupRule } from "./groupRules";
import { thetaApi, dataOf, pageOf, unwrapV1 } from "./theta";

/** The branded id the generated client wants in a verdict body (OSO-648). */
type GroupRuleId = GroupRule["id"];

type Schemas = components["schemas"];

/**
 * MODERATION, ON /api/v1 — §2.11
 *
 * The legacy surface put everything under `/theta/groups/:id/moderation/*` and
 * gave each verdict its own noun. v1 splits them by what they act on, and the
 * three verdicts hang off the FLAG rather than off the group:
 *
 *   POST   …/moderation/flag             ->  POST   /posts/{id}/flags
 *   GET    …/moderation/queue            ->  GET    /groups/{ref}/flags
 *   POST   …/flags/{id}/approve          ->  POST   /flags/{id}/dismiss
 *   POST   …/flags/{id}/reject           ->  POST   /flags/{id}/hide-post
 *   POST   …/flags/{id}/delete           ->  POST   /flags/{id}/delete-post
 *   GET/POST/DELETE …/moderation/words   ->  …/banned-words (cursor-paged)
 *   PUT    …/moderation/settings         ->  PATCH  /groups/{ref}
 *   GET/PUT …/moderation/notify          ->  the viewer's MEMBERSHIP row
 *   POST   …/members/{id}/temp_ban       ->  POST   /groups/{ref}/members/{id}/ban
 *   POST   …/members/{id}/expel          ->  POST   /groups/{ref}/members/{id}/expel
 *
 * THE VERDICT NAMES CHANGED BECAUSE THEY WERE WRONG. "Approve" meant "approve
 * the report" on one screen and "approve the post" on another; the queue's own
 * Danish labels ("Behold" / "Skjul" / "Slet") already said what actually
 * happens. `dismiss` closes the report and the content stands — which in
 * `approval_mode` PUBLISHES a post that was held for review.
 *
 * NO `total` ANYWHERE (D4). The pending count already has a home on
 * `Group.pendingCounts.flags`, computed by the same rule.
 *
 * A NON-MODERATOR GETS 404, not 403 (D7) — byte-identical to a group with no
 * queue, so the route cannot be walked to find out which groups have one.
 */
export type Flag = Schemas["Flag"];
export type FlagStatus = Schemas["FlagStatus"];
type BannedWord = Schemas["BannedWord"];
type GroupRef = string | number;

/* ------------------------------------------------------------------ flagging */

/**
 * Report a post or a comment. The GROUP IS DERIVED FROM THE POST, so a client
 * cannot name the wrong one — the legacy route took a group id in the path and
 * a post id in the body and trusted the pair.
 *
 * Refused for the author, for a moderator (who acts directly instead of
 * queueing work for themselves) and for a non-member; a second report of the
 * same post is `409 conflict`. All four are what `post.can.flag` already said,
 * so a button drawn from `can` never produces one.
 */
export function flagPost(
  postId: number,
  reason?: string,
  /** The rule the reporter says was broken (OSO-648); optional. */
  ruleId?: GroupRuleId | null,
): Promise<Flag> {
  return thetaApi.http
    .POST("/posts/{id}/flags", {
      params: { path: { id: postId } },
      body: { reason: reason ?? null, ruleId: ruleId ?? null },
    })
    .then((r) => dataOf<Flag>(r));
}

/* --------------------------------------------------------------- the queue */

export function getModerationQueue(
  ref: GroupRef,
  status: FlagStatus | "all" = "pending",
  cursor?: string | null,
): Promise<Page<Flag>> {
  return thetaApi.http
    .GET("/groups/{ref}/flags", {
      params: {
        path: { ref: String(ref) },
        query: { status, ...(cursor ? { cursor } : {}) },
      },
    })
    .then((r) => pageOf<Flag>(r));
}

/**
 * Close the report; the content stands. `409` if it is no longer pending.
 * No `ruleId` here on purpose: content that stands broke no rule (OSO-648),
 * and the server's `dismiss` body refuses the field.
 */
export function dismissFlag(flagId: number, note?: string): Promise<Flag> {
  return thetaApi.http
    .POST("/flags/{id}/dismiss", {
      params: { path: { id: flagId } },
      body: { note: note?.trim() ? note.trim() : null },
    })
    .then((r) => dataOf<Flag>(r));
}

/**
 * Uphold the report and hide the content, reversibly. `ruleId` cites the
 * group rule the content broke (OSO-648) — the author's notification then
 * says WHICH rule, which is the whole point of numbered rules.
 */
export function hideFlaggedPost(
  flagId: number,
  note?: string,
  ruleId?: GroupRuleId | null,
): Promise<Flag> {
  return removalVerdict("/flags/{id}/hide-post", flagId, note, ruleId);
}

/**
 * Uphold the report and delete the content.
 *
 * The response still renders what was removed, but as `content.state:
 * "removed"` and NOT as a post card: once deleted the row is filtered, so its
 * attachments and reactions are no longer readable and a card built for it
 * would be quietly missing them.
 */
export function deleteFlaggedPost(
  flagId: number,
  note?: string,
  ruleId?: GroupRuleId | null,
): Promise<Flag> {
  return removalVerdict("/flags/{id}/delete-post", flagId, note, ruleId);
}

function removalVerdict(
  path: "/flags/{id}/hide-post" | "/flags/{id}/delete-post",
  flagId: number,
  note?: string,
  ruleId?: GroupRuleId | null,
): Promise<Flag> {
  return thetaApi.http
    .POST(path, {
      params: { path: { id: flagId } },
      body: { note: note?.trim() ? note.trim() : null, ruleId: ruleId ?? null },
    })
    .then((r) => dataOf<Flag>(r));
}

/* ------------------------------------------------------------- the wordlist */

/**
 * Cursor-paged on `(word ASC, id ASC)`. A twelve-row list still gets a cursor:
 * D4 admits no exception for size, and retrofitting pagination onto a shipped
 * collection is the one thing that cannot be done additively.
 */
export function getBannedWords(
  ref: GroupRef,
  cursor?: string | null,
): Promise<Page<BannedWord>> {
  return thetaApi.http
    .GET("/groups/{ref}/banned-words", {
      params: { path: { ref: String(ref) }, query: cursor ? { cursor } : {} },
    })
    .then((r) => pageOf<BannedWord>(r));
}

/** Stored lower-cased, because the matcher compares lower-cased. Duplicate = 409. */
export function addBannedWord(ref: GroupRef, word: string): Promise<BannedWord> {
  return thetaApi.http
    .POST("/groups/{ref}/banned-words", {
      params: { path: { ref: String(ref) } },
      body: { word },
    })
    .then((r) => dataOf<BannedWord>(r));
}

export async function removeBannedWord(ref: GroupRef, wordId: number): Promise<void> {
  unwrapV1<null>(
    await thetaApi.http.DELETE("/groups/{ref}/banned-words/{id}", {
      params: { path: { ref: String(ref), id: wordId } },
    }),
  );
}

/* -------------------------------------------------------------- the settings */

/**
 * The moderation mode is a GROUP FIELD, not a moderation sub-resource — one
 * PATCH on the group, `§1.7`-style (name only what changes). It answers the
 * whole group, so the caller writes it back into the group cache rather than
 * refetching.
 */
export function updateModerationSettings(
  ref: GroupRef,
  moderationMode: "flag_mode" | "approval_mode",
): Promise<Schemas["Group"]> {
  return thetaApi.http
    .PATCH("/groups/{ref}", {
      params: { path: { ref: String(ref) } },
      body: { moderationMode },
    })
    .then((r) => dataOf<Schemas["Group"]>(r));
}

/**
 * "Notify me when content is flagged" is A PROPERTY OF MY MEMBERSHIP, not of
 * the group (§2.14) — three legacy verbs merged onto `PATCH
 * /groups/{ref}/membership`, and the read moved onto the membership row.
 *
 * `GET /groups/{ref}/members/{userId}` with my own id is the read; the PATCH
 * is the write and answers the updated row, so nothing needs refetching.
 */
export function getMembership(
  ref: GroupRef,
  userId: number,
): Promise<Schemas["Membership"]> {
  return thetaApi.http
    .GET("/groups/{ref}/members/{userId}", {
      params: { path: { ref: String(ref), userId } },
    })
    .then((r) => dataOf<Schemas["Membership"]>(r));
}

export function updateNotifyOnFlags(
  ref: GroupRef,
  notifyOnFlags: boolean,
): Promise<Schemas["Membership"]> {
  return thetaApi.http
    .PATCH("/groups/{ref}/membership", {
      params: { path: { ref: String(ref) } },
      body: { notifyOnFlags },
    })
    .then((r) => dataOf<Schemas["Membership"]>(r));
}
